import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, CardBody } from "@/Components/Common/Tailwind";
import { Skeleton } from "@/Components/ui/skeleton";
import { ChevronLeft, CircleAlert } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { noticeService } from "../../../api/services/notice.service";
import { stripHtmlTags } from "@/utils/text";
import moment from "moment";
import "moment/locale/ko";

moment.locale("ko");

// 카드 표면: 하드 보더 대신 다층 투명 그림자로 자연스러운 깊이 표현
const CARD_SHADOW =
  "shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_16px_-4px_rgba(0,0,0,0.08)]";
const CARD_SHADOW_HOVER =
  "hover:shadow-[0_2px_4px_rgba(0,0,0,0.05),0_12px_28px_-6px_rgba(0,0,0,0.12)]";

const NoticeList = (props) => {
  const navigate = useNavigate();
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await noticeService.getPublishedNotices();
        setNotices(response.data.results || []);
      } catch (error) {
        console.error("Failed to fetch notices:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return moment(dateStr).format("YYYY.MM.DD");
  };

  return (
    <div className="antialiased">
      <header className="sticky top-0 z-50 flex items-center gap-1 border-b border-gray-100 bg-white/90 px-2 py-2.5 backdrop-blur-sm">
        <button
          type="button"
          onClick={() => navigate("/")}
          aria-label="홈으로"
          className="flex h-10 w-10 items-center justify-center rounded-full text-gray-700 transition-[background-color,transform] hover:bg-gray-100 active:scale-[0.96]"
        >
          <ChevronLeft size={18} />
        </button>
        <h1 className="text-base font-bold text-gray-900">공지사항</h1>
      </header>

      <Container className="mt-4 px-3 sm:px-4">
        <Row className="justify-center">
          <Col>
            <Card id="noticeList" className="bg-transparent shadow-none border-0">
              <CardBody className="px-1 sm:px-2 pb-4 pt-2">
                {loading ? (
                  <div className="space-y-2.5">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className={`flex items-start gap-3 p-4 sm:p-5 rounded-2xl bg-white ${CARD_SHADOW}`}
                      >
                        <Skeleton className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl shrink-0" />
                        <div className="flex-1 min-w-0 space-y-2 py-1">
                          <Skeleton className="h-5 w-3/4 rounded" />
                          <Skeleton className="h-4 w-full rounded" />
                          <Skeleton className="h-3 w-20 rounded" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : notices.length === 0 ? (
                  <div className="animate-fade-up flex flex-col items-center justify-center px-6 py-20 text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                      <CircleAlert size={28} className="text-gray-400" />
                    </div>
                    <p className="mt-4 text-base font-bold text-gray-900 text-balance">
                      등록된 공지사항이 없어요
                    </p>
                    <p className="mt-1.5 text-sm text-gray-700 text-pretty">
                      새로운 소식이 올라오면 이곳에서 알려드릴게요.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {notices.map((notice, index) => {
                      const previewText = stripHtmlTags(notice.preview || "");
                      return (
                        <div
                          key={notice.id}
                          style={{ animationDelay: `${index * 60}ms` }}
                          className={`animate-fade-up group p-4 sm:p-5 rounded-2xl bg-white cursor-pointer transition-[box-shadow,transform] duration-200 active:scale-[0.98] ${CARD_SHADOW} ${CARD_SHADOW_HOVER}`}
                          onClick={() => navigate(`/notice/detail/${notice.id}`)}
                        >
                          <div className="flex items-start gap-3">
                            {notice.first_image_url && (
                              <img
                                src={notice.first_image_url}
                                alt=""
                                className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-xl shrink-0 outline outline-1 -outline-offset-1 outline-black/10"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <h5 className="font-semibold text-gray-900 line-clamp-2 mb-1 text-pretty">
                                <span
                                  className={
                                    notice.is_highlight
                                      ? "box-decoration-clone rounded-sm bg-[#fde68a] px-1"
                                      : undefined
                                  }
                                >
                                  {notice.title}
                                </span>
                              </h5>
                              {previewText && (
                                <p className="text-sm text-gray-600 line-clamp-2 mb-1 text-pretty">
                                  {previewText}
                                </p>
                              )}
                              <span className="text-xs text-gray-700 tabular-nums">
                                {formatDate(notice.display_date || notice.created_at)}
                              </span>
                            </div>
                            <div className="shrink-0 self-center">
                              <i className="ri-arrow-right-s-line inline-block text-gray-300 text-xl group-hover:text-gray-500 group-hover:translate-x-0.5 transition-[color,transform] duration-200"></i>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default NoticeList;
